import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VpnKey as VpnKeyIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import api from '../../utils/api';
import { en } from '../../translations/en';
import { featureFlags } from '../../utils/featureFlags';

type PublicAccessPolicy = 'auto' | 'blocked' | 'allowed';

interface HealthCheck {
  check: string;
  status: 'pass' | 'warn' | 'fail';
  message: string;
}

/** Per-catalog object count returned by admin_user_catalog_object_counts(). */
interface CatalogObjectCount {
  catalogAliId: string | null;
  creatorName: string | null;
  relationship: string | null;
  isPrimary: boolean;
  status: string | null;
  objectCount: number;
}

interface UserResult {
  userId: string;
  email: string | null;
  name: string | null;
  aliId: string | null;
  role: string | null;
  subscriptionPlan: string | null;
  createdAt: string | null;
  /** Per-catalog breakdown replacing the old scalar objectCount. */
  objectCatalogs: CatalogObjectCount[];
  collectionCount: number;
  delegationCount: number;
  cognitoStatus: string | null;
  accountEnabled: boolean;
  mfaEnabled: boolean;
  mfaMethods: string[];
  /** GH#1254 expiry lifecycle: derived deactivation timestamp (null = active). */
  deactivatedAt?: string | null;
  /** GH#1254 expiry lifecycle: tri-state public-visibility pin. */
  publicAccessPolicy?: 'auto' | 'blocked' | 'allowed' | null;
}

interface LookupResponse {
  user: UserResult;
  healthChecks: HealthCheck[];
}

interface UserSearchItem {
  userId: string;
  name: string | null;
  email: string | null;
  userType: string | null;
  aliId: string | null;
  artworkCount: number;
  profileImageUrl: string | null;
  createdAt: string | null;
  score: number;
}

type ActionType = 'lock' | 'unlock' | 'reset';

function detectQueryType(query: string): 'email' | 'ali_id' | 'name' {
  const trimmed = query.trim();
  if (trimmed.includes('@')) return 'email';
  if (/^[A-Z]{2}-\d[A-Z0-9]{2}$/i.test(trimmed)) return 'ali_id';
  return 'name';
}

const healthStatusToSeverity = (status: string): 'success' | 'warning' | 'error' => {
  if (status === 'pass') return 'success';
  if (status === 'warn') return 'warning';
  return 'error';
};

const t = en.admin.userLookup;

/** Compact per-catalog object count table shown in the Objects section. */
const CatalogObjectTable: React.FC<{ rows: CatalogObjectCount[] }> = ({ rows }) => {
  if (rows.length === 0) return null;

  const totalObjects = rows.reduce((sum, r) => sum + r.objectCount, 0);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t.objectCatalogsHeader}
      </Typography>
      <Table size="small" data-testid="catalog-object-table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t.catalogAliId}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t.catalogCreator}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t.catalogRelationship}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>{t.catalogCount}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={row.catalogAliId ?? `orphan-${idx}`}
              data-testid={row.catalogAliId ? `catalog-row-${row.catalogAliId}` : 'catalog-row-orphan'}
            >
              <TableCell>
                {row.catalogAliId ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {row.catalogAliId}
                    {row.isPrimary && (
                      <StarIcon
                        fontSize="inherit"
                        color="primary"
                        titleAccess={t.catalogPrimary}
                        data-testid="primary-star"
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t.catalogOrphan}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{row.creatorName ?? '—'}</TableCell>
              <TableCell>{row.relationship ?? '—'}</TableCell>
              <TableCell align="right">{row.objectCount}</TableCell>
            </TableRow>
          ))}
          {rows.length > 1 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                Total
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {totalObjects}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

const UserLookupTab: React.FC = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ActionType | null>(null);
  const [pendingPolicy, setPendingPolicy] = useState<PublicAccessPolicy | null>(null);
  const [candidates, setCandidates] = useState<UserSearchItem[] | null>(null);
  const [nameQuery, setNameQuery] = useState<string | null>(null);

  // GH#1254: the expiry-lifecycle admin surface (deactivated timestamp +
  // public-access-policy pin) is flag-gated until the prod flip at MR-8.
  const expiryLifecycleEnabled = featureFlags.isEnabled('enableExpiryLifecycle');

  const queryType = query.trim() ? detectQueryType(query) : null;

  const runAdminLookup = useCallback(async (q: string) => {
    const response = await api.get<LookupResponse>('/api/admin/users/lookup', {
      params: { q },
    });
    setResult(response.data);
    setCandidates(null);
    setNameQuery(null);
  }, []);

  const runNameSearch = useCallback(async (q: string) => {
    // GAP 2 fix (GL#41): use the admin search endpoint so soft-deleted users
    // are included in results. The public /api/search/users excludes them.
    const response = await api.get<{ total: number; items: UserSearchItem[] }>(
      '/api/admin/users/search',
      { params: { q, size: 25 } }
    );
    const items = response.data.items;
    setCandidates(items);
    setNameQuery(q);
  }, []);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setNotFound(false);
    setActionSuccess(null);
    setCandidates(null);
    setNameQuery(null);

    const qType = detectQueryType(trimmed);

    if (qType === 'name') {
      // Name query: call public search endpoint and show candidate list
      try {
        await runNameSearch(trimmed);
      } catch {
        setError(t.error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Email or ALI ID: direct admin lookup
    try {
      await runAdminLookup(trimmed);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        // Fall back to name search on 404 so admin can still find the user
        try {
          await runNameSearch(trimmed);
        } catch {
          setNotFound(true);
        }
      } else {
        setError(t.error);
      }
    } finally {
      setLoading(false);
    }
  }, [query, runAdminLookup, runNameSearch]);

  const handleCandidateClick = useCallback(async (candidate: UserSearchItem) => {
    const identifier = candidate.aliId ?? candidate.email;
    if (!identifier) return; // not administrable

    setLoading(true);
    setError(null);
    setActionSuccess(null);

    try {
      await runAdminLookup(identifier);
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }, [runAdminLookup]);

  const handleAction = useCallback(async (action: ActionType) => {
    if (!result) return;
    setConfirmDialog(null);
    setActionLoading(true);
    setActionSuccess(null);
    setError(null);

    try {
      const userId = result.user.userId;
      await api.post(`/api/admin/users/${userId}/${action === 'reset' ? 'reset-password' : action}`);

      const successMessages: Record<ActionType, string> = {
        lock: t.lockSuccess,
        unlock: t.unlockSuccess,
        reset: t.resetSuccess,
      };
      setActionSuccess(successMessages[action]);

      // Refresh user data after action — prefer aliId/email from result over raw query
      // (query might be a name if user arrived via candidate click)
      const refreshQ = result.user.aliId ?? result.user.email ?? query.trim();
      const response = await api.get<LookupResponse>('/api/admin/users/lookup', {
        params: { q: refreshQ },
      });
      setResult(response.data);
    } catch {
      setError(t.error);
    } finally {
      setActionLoading(false);
    }
  }, [result, query]);

  // GH#1254: pin/unpin the user's public visibility. Mirrors handleAction's
  // confirm → POST → success-banner → refetch shape, but carries a value.
  const handleSetPolicy = useCallback(async (policy: PublicAccessPolicy) => {
    if (!result) return;
    setPendingPolicy(null);
    setActionLoading(true);
    setActionSuccess(null);
    setError(null);

    try {
      const userId = result.user.userId;
      await api.post(`/api/admin/users/${userId}/public-access-policy`, { policy });
      setActionSuccess(t.policySuccess);

      const refreshQ = result.user.aliId ?? result.user.email ?? query.trim();
      const response = await api.get<LookupResponse>('/api/admin/users/lookup', {
        params: { q: refreshQ },
      });
      setResult(response.data);
    } catch {
      setError(t.error);
    } finally {
      setActionLoading(false);
    }
  }, [result, query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const confirmMessages: Record<ActionType, string> = {
    lock: t.lockConfirm,
    unlock: t.unlockConfirm,
    reset: t.resetConfirm,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search Section */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        {queryType && queryType !== 'name' && (
          <Chip
            label={queryType === 'email' ? 'Email' : 'ALI ID'}
            color={queryType === 'email' ? 'primary' : 'secondary'}
            size="small"
            sx={{ minWidth: 70 }}
          />
        )}
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={20} /> : t.search}
        </Button>
      </Box>

      {/* Feedback Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}
      {notFound && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.noResults}
        </Alert>
      )}

      {/* Name Search Candidate List */}
      {candidates !== null && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t.noNameMatches} &quot;{nameQuery}&quot;
              </Typography>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  {t.nameCandidatesTitle} &quot;{nameQuery}&quot;
                </Typography>
                <List disablePadding>
                  {candidates.map((candidate) => {
                    const isAdministrable = !!(candidate.aliId ?? candidate.email);
                    return (
                      <ListItemButton
                        key={candidate.userId}
                        onClick={() => isAdministrable && handleCandidateClick(candidate)}
                        disabled={!isAdministrable}
                        divider
                      >
                        <ListItemText
                          primary={candidate.name ?? '—'}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {candidate.aliId && (
                                <Chip label={candidate.aliId} size="small" color="secondary" />
                              )}
                              {candidate.email && (
                                <Typography component="span" variant="caption">
                                  {candidate.email}
                                </Typography>
                              )}
                              {candidate.userType && (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {candidate.userType}
                                </Typography>
                              )}
                              {!isAdministrable && (
                                <Chip
                                  label={t.candidateNotAdministrable}
                                  size="small"
                                  color="error"
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {result && (
        <>
          {/* User Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t.userInfo}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.name}</Typography>
                  <Typography variant="body1">{result.user.name || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.email}</Typography>
                  <Typography variant="body1">{result.user.email || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.aliId}</Typography>
                  <Typography variant="body1">
                    {result.user.aliId || (
                      <Chip label={t.notAssigned} color="error" size="small" />
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.role}</Typography>
                  <Typography variant="body1">{result.user.role || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.subscriptionPlan}</Typography>
                  <Typography variant="body1">{result.user.subscriptionPlan || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.createdAt}</Typography>
                  <Typography variant="body1">
                    {result.user.createdAt
                      ? new Date(result.user.createdAt).toLocaleDateString()
                      : '—'}
                  </Typography>
                </Grid>
                {expiryLifecycleEnabled && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t.deactivatedAt}</Typography>
                    <Typography variant="body1" data-testid="deactivated-at">
                      {result.user.deactivatedAt ? (
                        <Chip
                          label={new Date(result.user.deactivatedAt).toLocaleDateString()}
                          color="error"
                          size="small"
                        />
                      ) : (
                        '—'
                      )}
                    </Typography>
                  </Grid>
                )}

                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Per-catalog object breakdown */}
                <Grid size={12}>
                  <CatalogObjectTable rows={result.user.objectCatalogs} />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.collectionCount}</Typography>
                  <Typography variant="h6">{result.user.collectionCount}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t.delegationCount}</Typography>
                  <Typography variant="h6">{result.user.delegationCount}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Health Checks */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t.healthChecks}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.healthChecks.map((check) => (
                  <Alert
                    key={check.check}
                    severity={healthStatusToSeverity(check.status)}
                    sx={{ py: 0 }}
                  >
                    {check.message}
                  </Alert>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t.actions}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {result.user.accountEnabled ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LockIcon />}
                    onClick={() => setConfirmDialog('lock')}
                    disabled={actionLoading}
                  >
                    {t.lockAccount}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<LockOpenIcon />}
                    onClick={() => setConfirmDialog('unlock')}
                    disabled={actionLoading}
                  >
                    {t.unlockAccount}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<VpnKeyIcon />}
                  onClick={() => setConfirmDialog('reset')}
                  disabled={actionLoading}
                >
                  {t.resetPassword}
                </Button>
              </Box>
              {expiryLifecycleEnabled && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {t.publicAccessPolicy}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={result.user.publicAccessPolicy ?? 'auto'}
                    onChange={(_e, next: PublicAccessPolicy | null) => {
                      if (next && next !== (result.user.publicAccessPolicy ?? 'auto')) {
                        setPendingPolicy(next);
                      }
                    }}
                    disabled={actionLoading}
                    aria-label={t.publicAccessPolicy}
                    data-testid="public-access-policy-toggle"
                  >
                    <ToggleButton value="auto">{t.policyAuto}</ToggleButton>
                    <ToggleButton value="blocked">{t.policyBlocked}</ToggleButton>
                    <ToggleButton value="allowed">{t.policyAllowed}</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {t.policyHelp}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog !== null}
        onClose={() => setConfirmDialog(null)}
      >
        <DialogTitle>
          {confirmDialog === 'lock' && t.lockAccount}
          {confirmDialog === 'unlock' && t.unlockAccount}
          {confirmDialog === 'reset' && t.resetPassword}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog && confirmMessages[confirmDialog]}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>{t.no}</Button>
          <Button
            onClick={() => confirmDialog && handleAction(confirmDialog)}
            color={confirmDialog === 'unlock' ? 'success' : 'error'}
            variant="contained"
          >
            {t.yes}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Public-access-policy confirmation (GH#1254) */}
      <Dialog open={pendingPolicy !== null} onClose={() => setPendingPolicy(null)}>
        <DialogTitle>{t.publicAccessPolicy}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingPolicy && `${t.policyConfirm} "${pendingPolicy}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingPolicy(null)}>{t.no}</Button>
          <Button
            onClick={() => pendingPolicy && handleSetPolicy(pendingPolicy)}
            color="error"
            variant="contained"
          >
            {t.yes}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserLookupTab;
